'use client'

import { memo, useCallback } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { ChevronDown } from 'lucide-react'
import {
  Ninxl8BitAtmosphere,
  Ninxl8BitDivider,
  Ninxl8BitSection,
  Ninxl8BitThemeProvider,
  useNinxl8BitTheme,
  useNinxl8BitThemeMode,
} from '@/app/(marketing)/_components/8bit'
import { NinxlInkflowSection } from '@/app/ninxl/_components/ninxl-inkflow-section'
import { Ninxl8BitFooter } from '@/app/ninxl/_components/ninxl-8bit-footer'
import { NinxlScrollReveal } from '@/app/ninxl/_components/ninxl-scroll-reveal'
import { NinxlWordRotator } from '@/app/ninxl/_components/ninxl-scroll-motion'
import {
  NinxlHeroBeat,
  NinxlHeroEntrance,
  NinxlMagneticCta,
} from '@/app/ninxl/_components/ninxl-hero-entrance'
import { NinContactSection } from '@/app/(marketing)/_components/nin-contact-section'
import { MarketingMarquee } from '@/app/(marketing)/_components/marketing-marquee'
import {
  ExpandingFeatureCards,
  type ExpandingFeatureCard,
} from '@/app/(marketing)/_components/expanding-feature-cards'
import { MarketingLogoTicker } from '@/app/(marketing)/_components/marketing-logo-ticker'
import { MarketingFlipCards } from '@/app/(marketing)/_components/marketing-flip-cards'
import { MarketingOrbitRing } from '@/app/(marketing)/_components/marketing-orbit-ring'
import {
  MarketingCardMedia,
  NINK_CARD_MEDIA,
} from '@/app/(marketing)/_components/marketing-card-media'
import { MarketingContentMarquee } from '@/app/(marketing)/_components/marketing-content-marquee'
import { MarketingDemoTabs } from '@/app/(marketing)/_components/marketing-demo-tabs'
import { NINK_INKFLOW_DEMO_SRC } from '@/lib/landing-demos'
import { ScrollTilt } from '@/components/ui/container-scroll-animation'
import { Nink8BitNav } from './_components/nink-8bit-nav'
import { NinkHeroVisual } from './_components/nink-hero-visual'
import { NinkDownloadCta } from './_components/nink-download-cta'
import { NinkFaqSection } from './_components/nink-faq-section'
import { NinkFlowCompareSection } from './_components/nink-flow-compare-section'
import { NinkPrivacySection } from './_components/nink-privacy-section'
import { NinkWorksInSection } from './_components/nink-works-in-section'
import { NINK_DOCS_URL } from './_components/nink-links'
import {
  HardDrive,
  KeyRound,
  MessageSquare,
  Mic,
  ShieldCheck,
  WifiOff,
  FileText,
  Mail,
} from 'lucide-react'

const NINK_MARQUEE = [
  'Dictate',
  'Paste at cursor',
  'Whisper',
  'Slack',
  'WhatsApp',
  'Docs',
  'Mail',
  'Notes',
  'IDE',
  'On device',
] as const

const NINK_LOGO_TICKER = [
  { id: 'slack', label: 'Slack', icon: MessageSquare },
  { id: 'mail', label: 'Mail', icon: Mail },
  { id: 'docs', label: 'Docs', icon: FileText },
  { id: 'mic', label: 'Whisper', icon: Mic },
  { id: 'drive', label: 'On device', icon: HardDrive },
  { id: 'shield', label: 'Private', icon: ShieldCheck },
] as const

const NINK_ORBIT = [
  { id: 'mic', label: 'Mic', icon: Mic },
  { id: 'slack', label: 'Slack', icon: MessageSquare },
  { id: 'mail', label: 'Mail', icon: Mail },
  { id: 'docs', label: 'Docs', icon: FileText },
  { id: 'shield', label: 'Private', icon: ShieldCheck },
  { id: 'wifi', label: 'Offline capable', icon: WifiOff },
] as const

const NINK_FLIP_CARDS = [
  {
    id: 'audio',
    icon: Mic,
    frontTitle: 'Audio stays here',
    frontBody: 'Capture and Whisper run on your machine, not a cloud STT hop.',
    backTitle: 'On-device STT',
    backBody:
      'Raw Dictate never uploads audio or transcript to Google. Models land only after you download them.',
  },
  {
    id: 'keys',
    icon: KeyRound,
    frontTitle: 'Keys in the OS vault',
    frontBody: 'Optional BYOK uses Keychain / Credential Manager, not plaintext prefs.',
    backTitle: 'No silent cloud',
    backBody:
      'Local rewrite never auto-switches to cloud. Google AI runs only when you explicitly select it.',
  },
  {
    id: 'offline',
    icon: WifiOff,
    frontTitle: 'Works when offline',
    frontBody: 'Tray-first writing for the apps you already live in: even without a network.',
    backTitle: 'Privacy default',
    backBody:
      'Ink Flow is built for everyday messaging and docs without shipping your voice off-device.',
  },
] as const

const NINK_CONTENT_TILES = [
  { id: 'dictate', title: 'Dictate', subtitle: 'Hold Shift. Speak.', icon: Mic },
  { id: 'paste', title: 'Paste at cursor', subtitle: 'Land where you left off', icon: FileText },
  { id: 'whisper', title: 'Whisper local', subtitle: 'On-device STT', icon: HardDrive },
  { id: 'vault', title: 'OS vault keys', subtitle: 'Keychain / Credential Manager', icon: KeyRound },
  { id: 'offline', title: 'Offline capable', subtitle: 'No network required', icon: WifiOff },
  { id: 'private', title: 'Private default', subtitle: 'No silent cloud', icon: ShieldCheck },
] as const

const NINK_DEMO_TABS = [
  {
    id: 'howto',
    label: 'How to',
    title: 'Tray-first voice writing in three beats',
    body: 'Install NINK, grant mic access, then hold Shift and speak into the focused field.',
    bullets: [
      'Download and keep NINK in the menu bar / tray',
      'Download a Whisper model once (stays on disk)',
      'Dictate → rewrite locally → paste at the caret',
    ],
  },
  {
    id: 'demo',
    label: 'Demo',
    title: 'Ink Flow across everyday apps',
    body: 'Slack, Docs, Mail, Notes, and IDEs. NINK writes where you already work.',
    bullets: [
      'Speak a message without leaving Slack',
      'Draft email replies without a cloud STT hop',
      'Keep audio and transcript on your machine',
    ],
    media: (
      <video
        className="w-full"
        src={NINK_INKFLOW_DEMO_SRC}
        controls
        muted
        playsInline
        preload="metadata"
      />
    ),
  },
  {
    id: 'usecases',
    label: 'Use cases',
    title: 'Built for everyday writing, not demos',
    body: 'Voice writing for people who live in chat, docs, and tickets: with privacy as the default.',
    bullets: [
      'Faster replies in messaging apps',
      'Longer drafts in Docs and Notes',
      'Optional BYOK cloud rewrite only when you choose it',
    ],
  },
] as const

const NINK_EXPAND_CARDS: ExpandingFeatureCard[] = [
  {
    id: 'dictate',
    title: 'Dictate',
    tagline: 'Hold Shift. Speak.',
    description:
      'Ink Flow captures speech into the focused field: messages, docs, and tickets without leaving the app you are in.',
    ctaLabel: 'See Ink Flow',
  },
  {
    id: 'paste',
    title: 'Paste at cursor',
    tagline: 'Land where you left off',
    description:
      'Rewritten text drops at the caret. Switch apps mid-sentence; NINK keeps writing where you are looking.',
    ctaLabel: 'See the speed gap',
  },
  {
    id: 'local',
    title: 'Stay local',
    tagline: 'Whisper on your machine',
    description:
      'Speech-to-text and rewrite stay on device by default. Tray-first privacy for everyday writing.',
    ctaLabel: 'Privacy',
  },
]

const NinkLandingPageContent = memo(function NinkLandingPageContent() {
  const { theme } = useNinxl8BitTheme()
  const themeMode = useNinxl8BitThemeMode()
  const logoSrc =
    themeMode === 'dark' ? '/nink_logo_dark.svg' : '/nink_logo_light.svg'

  const scrollTo = useCallback((id: string) => {
    const target =
      document.getElementById(id) ??
      document.querySelector<HTMLElement>(`[data-section="${id}"]`)
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  return (
    <div
      className="nin-landing ninxl-landing ninxl-8bit nink-hybrid min-h-screen overflow-x-hidden"
      data-ninxl-theme={theme}
    >
      <Ninxl8BitAtmosphere />
      <Nink8BitNav onScrollTo={scrollTo} />

      {/* HERO: premium hierarchy; 8-bit lives inside the product stage */}
      <section data-section="hero" className="relative pt-16 pb-16 md:pt-24 md:pb-20">
        <NinxlHeroEntrance className="mx-auto max-w-4xl px-6 text-center">
          <NinxlHeroBeat variant="cartridge">
            <div className="nink-hybrid-chip mb-8 inline-flex items-center gap-2">
              <Image
                key={themeMode}
                src={logoSrc}
                alt=""
                width={14}
                height={14}
                className="h-3.5 w-3.5"
                aria-hidden
              />
              <span className="nink-hybrid-eyebrow !tracking-[0.12em]">
                Voice writing · on device
              </span>
            </div>
          </NinxlHeroBeat>

          <NinxlHeroBeat>
            <h1 className="nink-hybrid-display ninxl-theme-text mx-auto mb-6 max-w-3xl text-4xl sm:text-5xl md:text-[4.25rem]">
              Don’t type.
              <br />
              <span className="ninxl-theme-text-muted">Just speak into</span>{' '}
              <NinxlWordRotator
                words={['WhatsApp.', 'Slack.', 'Docs.', 'Mail.', 'Notes.', 'your IDE.']}
                className="ninxl-theme-text-accent"
              />
            </h1>
          </NinxlHeroBeat>

          <NinxlHeroBeat>
            <p className="nink-hybrid-body mx-auto mb-10 max-w-xl text-base md:text-lg">
              NINK turns speech into keystrokes on the desktop you already live in.
              Switch apps, keep talking: everything stays local.
            </p>
          </NinxlHeroBeat>

          <NinxlHeroBeat>
            <div className="mb-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <NinxlMagneticCta>
                <button
                  type="button"
                  onClick={() => scrollTo('download')}
                  className="nink-hybrid-btn nink-hybrid-btn-primary"
                >
                  Download NINK
                </button>
              </NinxlMagneticCta>
              <NinxlMagneticCta>
                <button
                  type="button"
                  onClick={() => scrollTo('inkflow')}
                  className="nink-hybrid-btn"
                >
                  See how it works
                </button>
              </NinxlMagneticCta>
              <NinxlMagneticCta>
                <Link href={NINK_DOCS_URL} className="nink-hybrid-btn">
                  Docs
                </Link>
              </NinxlMagneticCta>
            </div>
          </NinxlHeroBeat>
        </NinxlHeroEntrance>

        <NinxlHeroBeat variant="stage" className="relative mx-auto max-w-[1060px] px-4 sm:px-8">
          <MarketingOrbitRing items={NINK_ORBIT}>
            <ScrollTilt>
              <NinkHeroVisual onDownload={() => scrollTo('download')} />
            </ScrollTilt>
          </MarketingOrbitRing>
        </NinxlHeroBeat>

        <NinxlScrollReveal delay={0.15} variant="blur">
          <div id="download" className="relative z-10 mx-auto mt-14 max-w-lg scroll-mt-24 px-4">
            <div className="nink-hybrid-card px-6 py-8 text-center">
              <div className="nink-hybrid-eyebrow mb-3 justify-center">Install</div>
              <h2 className="nink-hybrid-display ninxl-theme-text mb-2 text-2xl md:text-3xl">
                Mac + Windows
              </h2>
              <p className="nink-hybrid-body mb-6 text-sm md:text-base">
                On device. Ready in a minute.
              </p>
              <NinkDownloadCta />
              <p className="nink-hybrid-body mt-6 text-sm">
                Need chat + tools too?{' '}
                <Link href="/ninxl" className="ninxl-theme-link font-semibold">
                  Get NINX
                </Link>
              </p>
            </div>
          </div>
        </NinxlScrollReveal>

        <NinxlScrollReveal delay={0.22} variant="rise" className="mt-12">
          <MarketingMarquee items={NINK_MARQUEE} label="NINK highlights" />
        </NinxlScrollReveal>

        <NinxlScrollReveal delay={0.28} variant="rise" className="mt-6">
          <MarketingLogoTicker items={NINK_LOGO_TICKER} label="NINK works in" />
        </NinxlScrollReveal>

        <button
          type="button"
          onClick={() => scrollTo('inkflow')}
          className="relative z-10 mx-auto mt-12 flex flex-col items-center gap-2 border-0 bg-transparent cursor-pointer text-[color:var(--ninxl-8bit-muted)] transition-colors hover:text-[color:var(--ninxl-theme-fg)]"
        >
          <span className="nink-hybrid-body text-sm font-medium">Explore Ink Flow</span>
          <ChevronDown className="ninxl-explore-chevron h-4 w-4" />
        </button>
      </section>

      <Ninxl8BitDivider />

      <Ninxl8BitSection id="inkflow" data-section="inkflow" className="py-20">
        {/* baby: Ribbon already lives in NinkFlowCompareSection below */}
        <NinxlInkflowSection showVoiceRibbon={false} />
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <Ninxl8BitSection alt data-section="expand" className="py-20">
        <ExpandingFeatureCards
          label="INK FLOW"
          title="Dictate. Land. Stay private."
          description="Hover to expand each beat of the voice-writing loop."
          cards={NINK_EXPAND_CARDS.map((card) =>
            card.id === 'paste'
              ? { ...card, onClick: () => scrollTo('speed') }
              : card.id === 'local'
                ? { ...card, onClick: () => scrollTo('privacy') }
                : { ...card, onClick: () => scrollTo('inkflow') },
          )}
          renderMedia={(card, active) => {
            const kind = NINK_CARD_MEDIA[card.id]
            return kind ? <MarketingCardMedia kind={kind} active={active} /> : null
          }}
        />
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <Ninxl8BitSection data-section="content-marquee" className="py-10 md:py-14">
        <MarketingContentMarquee
          label="NINK capabilities"
          tiles={NINK_CONTENT_TILES}
        />
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <Ninxl8BitSection data-section="guide" className="py-16 md:py-20">
        <MarketingDemoTabs
          label="START HERE"
          title="How to, demo, and use cases."
          description="Tray voice writing without shipping audio to a cloud STT hop."
          tabs={NINK_DEMO_TABS}
        />
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <Ninxl8BitSection id="speed" data-section="speed" className="scroll-mt-24 py-20">
        <NinkFlowCompareSection />
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <Ninxl8BitSection id="works-in" data-section="works-in" className="scroll-mt-24 py-20">
        <NinkWorksInSection />
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <Ninxl8BitSection data-section="flip" className="scroll-mt-24 py-20">
        <MarketingFlipCards
          label="LOCAL BY DESIGN"
          title="Flip for the privacy details."
          description="Hover or tap: audio, keys, and offline defaults without the cloud hop."
          cards={NINK_FLIP_CARDS}
        />
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <Ninxl8BitSection id="privacy" data-section="privacy" className="scroll-mt-24 py-20">
        <NinkPrivacySection />
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <Ninxl8BitSection id="faq" data-section="faq" className="py-20">
        <NinkFaqSection />
      </Ninxl8BitSection>

      <Ninxl8BitDivider />

      <Ninxl8BitSection id="contact" data-section="contact" className="scroll-mt-24 py-20">
        <div className="mx-auto max-w-6xl px-6">
          <NinContactSection variant="org" />
        </div>
      </Ninxl8BitSection>

      <Ninxl8BitFooter />
    </div>
  )
})

const NinkLandingPage = () => (
  <Ninxl8BitThemeProvider>
    <NinkLandingPageContent />
  </Ninxl8BitThemeProvider>
)

export default NinkLandingPage
